/** @file Computes the bone/constraint update order: DFS interleaving, dependency sort, cycle rejection. */
import type { Doc, DocIndex, Pose2DJson } from '@pose2d/format/types';
import { compiledBoneParents } from './compiledBoneParents';
import { interleaveUpdateOps } from './interleaveUpdateOps';
import { requireCompiledIndex } from './requireCompiledIndex';
import { sortIkChains } from './sortIkChains';
import type { IkChain } from './sortIkChains';

/** Returns the compiled op list; throws CYCLE on unsolvable dependencies. */
export function compileUpdateOrder(doc: Doc, index: DocIndex, boneIndex: Map<string, number>, ikIndex: Map<string, number>): Pose2DJson['updateOrder'] {
  const parents = compiledBoneParents(doc, boneIndex);
  const chains = Object.entries(doc.constraints).map(([id, c]): IkChain => ({
    id,
    name: c.name,
    ik: requireCompiledIndex(ikIndex, id, 'ik', `constraints.${id}`),
    bones: c.bones.map((b, i) => requireCompiledIndex(boneIndex, b, 'bone', `constraints.${id}.bones.${i}`)),
    target: requireCompiledIndex(boneIndex, c.target, 'bone', `constraints.${id}.target`),
  }));
  chains.sort((a, b) => a.ik - b.ik);
  return interleaveUpdateOps(parents, sortIkChains(chains, parents));
}
